import type { PbVideoSource } from "../types";

const YOUTUBE =
  /^(?:https?:)?\/\/(?:www\.|m\.)?(youtube(?:-nocookie)?\.com|youtu\.be)\/(?:embed\/|v\/|watch\?(?:.*&)?v=|shorts\/)?([\w-]{11})/i;
const VIMEO = /^(?:https?:)?\/\/(?:www\.|player\.)?vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?(\d+)/i;
const FILE_EXT = /\.(mp4|webm|ogv|ogg)(?:[?#].*)?$/i;

/**
 * Parses a Page Builder video source:
 * - video content type: `https://www.youtube.com/embed/<id>?…`, Vimeo player URL, or a file URL,
 * - video backgrounds (`data-video-src`): same providers, or `mp4:<url>,webm:<url>,ogv:<url>`.
 */
export function parseVideoSource(raw: string | undefined | null): PbVideoSource | null {
  const src = (raw ?? "").trim();
  if (!src) return null;

  if (/^(?:mp4|webm|ogv):/i.test(src)) {
    const sources = src
      .split(/,(?=\s*(?:mp4|webm|ogv):)/i)
      .map((part) => {
        const m = part.trim().match(/^(mp4|webm|ogv):(.+)$/i);
        if (!m) return null;
        const ext = m[1].toLowerCase();
        return { type: ext === "ogv" ? "video/ogg" : `video/${ext}`, url: m[2].trim() };
      })
      .filter(
        (s): s is { type: string; url: string } =>
          !!s && (/^(?:https?:)?\/\//i.test(s.url) || s.url.startsWith("/"))
      );
    return sources.length ? { provider: "file", sources } : null;
  }

  const yt = src.match(YOUTUBE);
  if (yt) {
    return {
      provider: "youtube",
      id: yt[2],
      noCookie: /nocookie/i.test(yt[1]),
      url: src.startsWith("//") ? `https:${src}` : src,
    };
  }

  const vimeo = src.match(VIMEO);
  if (vimeo) return { provider: "vimeo", id: vimeo[1], url: src.startsWith("//") ? `https:${src}` : src };

  const ext = src.match(FILE_EXT);
  if (ext && /^(?:https?:)?\/\//i.test(src)) {
    const e = ext[1].toLowerCase();
    return { provider: "file", sources: [{ type: e === "ogv" || e === "ogg" ? "video/ogg" : `video/${e}`, url: src }] };
  }

  return null;
}

/** Embed URL for a background video (muted, looping, chrome-less, JS API enabled). */
export function backgroundEmbedUrl(source: PbVideoSource, loop: boolean): string | null {
  if (source.provider === "youtube") {
    const host = source.noCookie ? "www.youtube-nocookie.com" : "www.youtube.com";
    const params = new URLSearchParams({
      autoplay: "1",
      mute: "1",
      controls: "0",
      playsinline: "1",
      rel: "0",
      modestbranding: "1",
      iv_load_policy: "3",
      disablekb: "1",
      enablejsapi: "1",
    });
    if (loop) {
      params.set("loop", "1");
      params.set("playlist", source.id);
    }
    return `https://${host}/embed/${source.id}?${params.toString()}`;
  }
  if (source.provider === "vimeo") {
    const params = new URLSearchParams({
      background: "1",
      autoplay: "1",
      muted: "1",
      loop: loop ? "1" : "0",
      autopause: "0",
      playsinline: "1",
      dnt: "1",
    });
    return `https://player.vimeo.com/video/${source.id}?${params.toString()}`;
  }
  return null;
}
